import React, { useMemo, useState } from 'react';
import { apiGet } from '../../api';
import { useAsync } from '../../hooks/useAsync';
import { useToast } from '../../components/ToastHost';
import { fundraisingStageLabel, probabilityLabel } from '../../i18n';
import type { Company, FundraisingStage, Probability } from '../../types';

type DailyEmailMetrics = {
  day: string;
  sessions: number;
  uniquePeople: number;
  fileViews: number;
  downloads: number;
  emailsSent: number;
  emailsClicked: number;
  emailsOpened?: number;
  emailsBounced?: number;
  emailsReplied?: number;
};

type OverviewResp = {
  days: number;
  since: string;
  summary: {
    totalEvents: number;
    lastEventAt: string | null;
    sessions: number;
    uniquePeople: number;
    downloads: number;
    views: number;
    emailsSent: number;
    emailsClicked: number;
    emailsOpened?: number;
    emailsBounced?: number;
    emailsReplied?: number;
  };
  recentAccesses: Array<{
    companyId: string;
    companyName: string;
    companyStage: FundraisingStage;
    companyProbability: Probability;
    personId: string | null;
    personName: string | null;
    personEmail: string | null;
    sessions: number;
    firstSessionAt: string | null;
    lastSessionAt: string | null;
    accessLinkUrl: string | null;
  }>;
  daily: DailyEmailMetrics[];
  bouncedEmails?: Array<{
    emailId: string;
    subject: string;
    toEmail: string;
    createdAt: string;
    bouncedAt: string;
    bounceType: string | null;
    bounceSubType: string | null;
    bounceMessage: string | null;
    personId: string;
    personName: string;
    companyId: string;
    companyName: string;
  }>;
};

type CompanyResp = {
  company: { id: string; name: string };
  days: number;
  since: string;
  summary: {
    totalEvents: number;
    firstEventAt: string | null;
    lastEventAt: string | null;
    sessions: number;
    firstSessionAt: string | null;
    lastSessionAt: string | null;
    uniquePeople: number;
    downloads: number;
    views: number;
    kpisViews: number;
    dataRoomViews: number;
    emailsSent: number;
    emailsClicked: number;
    emailsOpened?: number;
    emailsBounced?: number;
    emailsReplied?: number;
  };
  daily: Array<{
    day: string;
    sessions: number;
    uniquePeople: number;
    fileViews: number;
    downloads: number;
    kpisViews: number;
    dataRoomViews: number;
    emailsSent: number;
    emailsClicked: number;
    emailsOpened?: number;
    emailsBounced?: number;
    emailsReplied?: number;
  }>;
};

function fmtNum(v: any) {
  const n = Number(v || 0);
  return new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 }).format(Number.isFinite(n) ? n : 0);
}

function fmtTs(ts: string | null | undefined) {
  if (!ts) return '—';
  return ts.slice(0, 19).replace('T', ' ');
}

function fmtAgo(ts: string | null | undefined) {
  if (!ts) return '—';
  const diffMs = Math.max(0, Date.now() - Date.parse(ts));
  if (!Number.isFinite(diffMs)) return '—';
  const mins = Math.floor(diffMs / 60000);
  if (mins < 1) return 'Just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `${days}d ago`;
  const months = Math.floor(days / 30);
  return `${months}mo ago`;
}

function seriesColor(kind: 'sent' | 'bounced' | 'replied') {
  if (kind === 'sent') return '#1b365d';
  if (kind === 'bounced') return '#dc2626';
  return '#0ea5a3';
}

function buildPoints(values: number[], width: number, height: number, max: number) {
  if (values.length === 1) return `0,${height / 2}`;
  return values
    .map((value, index) => {
      const x = (index / Math.max(1, values.length - 1)) * width;
      const y = height - (value / max) * height;
      return `${x},${y}`;
    })
    .join(' ');
}

function EmailLineChart({ items }: { items: DailyEmailMetrics[] }) {
  const width = 720;
  const height = 220;
  const labels = items.map((item) => item.day.slice(5));
  const sent = items.map((item) => Number(item.emailsSent || 0));
  const bounced = items.map((item) => Number(item.emailsBounced || 0));
  const replied = items.map((item) => Number(item.emailsReplied || 0));
  const max = Math.max(1, ...sent, ...bounced, ...replied);
  const tickCount = Math.min(5, Math.max(2, items.length));
  const tickIndexes = Array.from({ length: tickCount }, (_, i) => Math.round((i * Math.max(0, items.length - 1)) / Math.max(1, tickCount - 1)));

  if (!items.length) return <div className="muted">No email data yet.</div>;

  return (
    <div className="email-line-chart">
      <div className="email-line-chart__legend">
        <span className="email-line-chart__legend-item">
          <span className="email-line-chart__swatch" style={{ background: seriesColor('sent') }} /> Sent
        </span>
        <span className="email-line-chart__legend-item">
          <span className="email-line-chart__swatch" style={{ background: seriesColor('bounced') }} /> Bounced
        </span>
        <span className="email-line-chart__legend-item">
          <span className="email-line-chart__swatch" style={{ background: seriesColor('replied') }} /> Replied
        </span>
      </div>
      <svg viewBox={`0 0 ${width} ${height + 28}`} className="email-line-chart__svg" role="img" aria-label="Emails sent, bounced, and replied per day">
        {[0, 0.25, 0.5, 0.75, 1].map((ratio) => {
          const y = height - ratio * height;
          return (
            <g key={ratio}>
              <line x1="0" y1={y} x2={width} y2={y} stroke="rgba(27,54,93,0.10)" strokeWidth="1" />
              <text x="0" y={Math.max(12, y - 6)} fill="rgba(34,34,34,0.58)" fontSize="11">
                {fmtNum(Math.round(ratio * max))}
              </text>
            </g>
          );
        })}
        <polyline fill="none" stroke={seriesColor('sent')} strokeWidth="3" points={buildPoints(sent, width, height, max)} />
        <polyline fill="none" stroke={seriesColor('bounced')} strokeWidth="3" points={buildPoints(bounced, width, height, max)} />
        <polyline fill="none" stroke={seriesColor('replied')} strokeWidth="3" points={buildPoints(replied, width, height, max)} />
        {tickIndexes.map((index) => {
          const x = items.length === 1 ? 0 : (index / Math.max(1, items.length - 1)) * width;
          return (
            <text key={`${labels[index]}:${index}`} x={x} y={height + 20} textAnchor={index === 0 ? 'start' : index === items.length - 1 ? 'end' : 'middle'} fill="rgba(34,34,34,0.58)" fontSize="11">
              {labels[index]}
            </text>
          );
        })}
      </svg>
    </div>
  );
}

export default function AdminReports() {
  const [days, setDays] = useState(30);
  const toast = useToast();
  const { data: companiesData } = useAsync<{ companies: Company[] }>(() => apiGet('/api/admin/companies'), []);
  const companies = useMemo(() => (companiesData?.companies || []).slice().sort((a, b) => a.name.localeCompare(b.name)), [companiesData]);

  const { data: overview, loading: loadingOv, error: errOv } = useAsync<OverviewResp>(
    () => apiGet(`/api/admin/reports/overview?days=${days}`),
    [days]
  );

  const [companyId, setCompanyId] = useState<string>('');
  const activeCompanyId = companyId || companies[0]?.id || '';

  const { data: companyReport, loading: loadingCo, error: errCo } = useAsync<CompanyResp>(
    () => (activeCompanyId ? apiGet(`/api/admin/reports/company/${activeCompanyId}?days=${days}`) : Promise.resolve(null as any)),
    [activeCompanyId, days]
  );

  const emailAnalytics = useMemo(
    () =>
      (overview?.daily || []).map((item) => ({
        ...item,
        emailsBounced: Number(item.emailsBounced || 0),
        emailsReplied: Number(item.emailsReplied || 0),
      })),
    [overview]
  );

  const copyAccessLink = async (url: string) => {
    try {
      await navigator.clipboard.writeText(url);
      toast.success('Link copied.');
    } catch {
      toast.info('Copy not supported in this browser.');
    }
  };

  return (
    <div className="grid" style={{ gap: 14 }}>
      <div className="card">
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
          <div>
            <div style={{ fontSize: 18, fontWeight: 900 }}>Reports</div>
            <div className="muted" style={{ fontSize: 12 }}>
              Activity timeline + investor detail
            </div>
          </div>
          <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
            <span className="muted" style={{ fontSize: 12 }}>
              Window
            </span>
            <select className="select" value={days} onChange={(e) => setDays(parseInt(e.target.value, 10))} style={{ width: 160 }}>
              <option value={7}>7 days</option>
              <option value={14}>14 days</option>
              <option value={30}>30 days</option>
              <option value={60}>60 days</option>
              <option value={90}>90 days</option>
            </select>
          </div>
        </div>
      </div>

      <div className="card">
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', alignItems: 'baseline' }}>
          <div>
            <div style={{ fontSize: 16, fontWeight: 900 }}>Recent investor access</div>
            <div className="muted" style={{ fontSize: 12 }}>
              Latest investors who logged in during the selected window
            </div>
          </div>
          {overview?.recentAccesses?.length ? (
            <div className="muted" style={{ fontSize: 12 }}>
              {overview.recentAccesses.length} investor{overview.recentAccesses.length === 1 ? '' : 's'} active
            </div>
          ) : null}
        </div>
        <div style={{ height: 12 }} />
        {loadingOv ? (
          <div className="muted">Loading…</div>
        ) : errOv ? (
          <div className="muted">Error: {errOv}</div>
        ) : !overview?.recentAccesses?.length ? (
          <div className="muted">No investor logins in this window.</div>
        ) : (
          <table className="table">
            <thead>
              <tr>
                <th>When</th>
                <th>Investor</th>
                <th>Last contact</th>
                <th>Sessions</th>
                <th>Follow-up</th>
              </tr>
            </thead>
            <tbody>
              {overview.recentAccesses.map((item) => (
                <tr key={`${item.companyId}:${item.lastSessionAt || item.firstSessionAt || 'n/a'}`}>
                  <td>
                    <div style={{ fontWeight: 800, whiteSpace: 'nowrap' }}>{fmtTs(item.lastSessionAt)}</div>
                    <div className="muted" style={{ fontSize: 12 }}>
                      {fmtAgo(item.lastSessionAt)}
                    </div>
                  </td>
                  <td>
                    <div style={{ fontWeight: 800 }}>{item.companyName}</div>
                    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 4 }}>
                      <span className="pill">{fundraisingStageLabel(item.companyStage)}</span>
                      <span className="pill">Prob: {probabilityLabel(item.companyProbability)}</span>
                    </div>
                  </td>
                  <td>
                    <div style={{ fontWeight: 800 }}>{item.personName || 'Unknown contact'}</div>
                    <div className="muted" style={{ fontSize: 12 }}>
                      {item.personEmail || 'No email'}
                    </div>
                  </td>
                  <td>
                    <div style={{ fontWeight: 800 }}>{fmtNum(item.sessions)}</div>
                    <div className="muted" style={{ fontSize: 12 }}>
                      in {days} days
                    </div>
                  </td>
                  <td>
                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                      <a className="btn" href={`/admin/companies/${item.companyId}`}>
                        Open CRM
                      </a>
                      {item.accessLinkUrl ? (
                        <a className="btn" href={item.accessLinkUrl} target="_blank" rel="noreferrer">
                          Open link
                        </a>
                      ) : null}
                      {item.accessLinkUrl ? (
                        <button className="btn" type="button" onClick={() => copyAccessLink(item.accessLinkUrl!)}>
                          Copy link
                        </button>
                      ) : null}
                    </div>
                    {!item.accessLinkUrl ? (
                      <div className="muted" style={{ fontSize: 12, marginTop: 6 }}>
                        No active investor link.
                      </div>
                    ) : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <div className="card">
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', alignItems: 'baseline' }}>
          <div>
            <div style={{ fontSize: 16, fontWeight: 900 }}>Email analytics</div>
            <div className="muted" style={{ fontSize: 12 }}>
              Daily sent / bounced / replied
            </div>
          </div>
          {overview ? (
            <div className="muted" style={{ fontSize: 12 }}>
              Sent: {fmtNum(overview.summary.emailsSent)} · Opened: {fmtNum(overview.summary.emailsOpened)} · Bounced: {fmtNum(overview.summary.emailsBounced)} · Replied:{' '}
              {fmtNum(overview.summary.emailsReplied)}
            </div>
          ) : null}
        </div>
        <div style={{ height: 12 }} />
        {loadingOv ? (
          <div className="muted">Loading…</div>
        ) : errOv ? (
          <div className="muted">Error: {errOv}</div>
        ) : !overview ? (
          <div className="muted">No data.</div>
        ) : (
          <div className="grid" style={{ gap: 12 }}>
            <EmailLineChart items={emailAnalytics} />
            <div className="grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))' }}>
              <MiniStat title="Sent" value={fmtNum(overview.summary.emailsSent)} />
              <MiniStat title="Opened" value={fmtNum(overview.summary.emailsOpened)} />
              <MiniStat title="Bounced" value={fmtNum(overview.summary.emailsBounced)} />
              <MiniStat title="Replied" value={fmtNum(overview.summary.emailsReplied)} />
            </div>
          </div>
        )}
      </div>

      <div className="card">
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', alignItems: 'baseline' }}>
          <div>
            <div style={{ fontSize: 16, fontWeight: 900 }}>Bounced emails</div>
            <div className="muted" style={{ fontSize: 12 }}>
              Investors to retry via another channel
            </div>
          </div>
          {overview?.bouncedEmails?.length ? (
            <div className="pill">{overview.bouncedEmails.length} in window</div>
          ) : null}
        </div>
        <div style={{ height: 12 }} />
        {loadingOv ? (
          <div className="muted">Loading…</div>
        ) : !overview?.bouncedEmails?.length ? (
          <div className="muted">No bounces in this window.</div>
        ) : (
          <table className="table">
            <thead>
              <tr>
                <th>When</th>
                <th>Investor</th>
                <th>Person</th>
                <th>Reason</th>
              </tr>
            </thead>
            <tbody>
              {overview.bouncedEmails.map((item) => (
                <tr key={`${item.emailId}:${item.bouncedAt}`}>
                  <td className="muted" style={{ whiteSpace: 'nowrap' }}>
                    {fmtTs(item.bouncedAt)}
                  </td>
                  <td>
                    <div style={{ fontWeight: 800 }}>{item.companyName}</div>
                    <div className="muted" style={{ fontSize: 12 }}>
                      {item.subject}
                    </div>
                  </td>
                  <td>
                    <div style={{ fontWeight: 800 }}>{item.personName}</div>
                    <div className="muted" style={{ fontSize: 12 }}>
                      {item.toEmail}
                    </div>
                  </td>
                  <td className="muted">
                    {[item.bounceType, item.bounceSubType].filter(Boolean).join(' / ') || 'Bounce'}
                    {item.bounceMessage ? ` — ${item.bounceMessage}` : ''}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <div className="card">
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', alignItems: 'baseline' }}>
          <div>
            <div style={{ fontSize: 16, fontWeight: 900 }}>Timeline</div>
            <div className="muted" style={{ fontSize: 12 }}>
              Daily activity (total)
            </div>
          </div>
          {overview ? (
            <div className="muted" style={{ fontSize: 12 }}>
              Total sessions: {fmtNum(overview.summary.sessions)} · unique: {fmtNum(overview.summary.uniquePeople)} · downloads: {fmtNum(overview.summary.downloads)}
            </div>
          ) : null}
        </div>
        <div style={{ height: 12 }} />
        {loadingOv ? (
          <div className="muted">Loading…</div>
        ) : errOv ? (
          <div className="muted">Error: {errOv}</div>
        ) : !overview ? (
          <div className="muted">No data.</div>
        ) : (
          <div className="grid" style={{ gap: 10 }}>
            {overview.daily.map((d) => (
              <div
                key={d.day}
                style={{
                  display: 'flex',
                  gap: 10,
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: '10px 12px',
                  border: '1px solid var(--border)',
                  borderRadius: 14,
                }}
              >
                <div style={{ fontWeight: 800, fontVariantNumeric: 'tabular-nums' }}>{d.day}</div>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                  <span className="pill">Sessions: {fmtNum(d.sessions)}</span>
                  <span className="pill">Unique: {fmtNum(d.uniquePeople)}</span>
                  <span className="pill">Files: {fmtNum(d.fileViews)}</span>
                  <span className="pill">Downloads: {fmtNum(d.downloads)}</span>
                  <span className="pill">Emails: {fmtNum(d.emailsSent)}</span>
                  <span className="pill">Clicks: {fmtNum(d.emailsClicked)}</span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="card">
        <div style={{ fontSize: 16, fontWeight: 900, marginBottom: 10 }}>Investor detail</div>
        <div className="muted" style={{ fontSize: 12, marginBottom: 6 }}>
          Investor
        </div>
        <select className="select" value={activeCompanyId} onChange={(e) => setCompanyId(e.target.value)}>
          {companies.map((c) => (
            <option key={c.id} value={c.id}>
              {c.name}
            </option>
          ))}
        </select>
        <div style={{ height: 12 }} />

        {loadingCo ? (
          <div className="muted">Loading…</div>
        ) : errCo ? (
          <div className="muted">Error: {errCo}</div>
        ) : !companyReport ? (
          <div className="muted">No data.</div>
        ) : (
          <>
            <div className="grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))' }}>
              <MiniStat title="First visit" value={fmtTs(companyReport.summary.firstSessionAt || companyReport.summary.firstEventAt)} />
              <MiniStat title="Last visit" value={fmtTs(companyReport.summary.lastSessionAt || companyReport.summary.lastEventAt)} />
              <MiniStat title="Sessions (total)" value={fmtNum(companyReport.summary.sessions)} />
              <MiniStat title="Unique people" value={fmtNum(companyReport.summary.uniquePeople)} />
            </div>
            <div style={{ height: 10 }} />
            <div className="grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))' }}>
              <MiniStat title="Metrics Dashboard" value={fmtNum(companyReport.summary.kpisViews)} />
              <MiniStat title="Data Room" value={fmtNum(companyReport.summary.dataRoomViews)} />
              <MiniStat title="Visited" value={companyReport.summary.sessions > 0 ? 'Yes' : 'No'} />
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function MiniStat({ title, value }: { title: string; value: string }) {
  return (
    <div className="card" style={{ margin: 0 }}>
      <h3>{title}</h3>
      <div className="metric">{value}</div>
    </div>
  );
}
